<script setup>
import { computed, watch } from 'vue'
import { useAppStore } from '@/stores/app'

const props = defineProps({
	// The property to control the expanded state from the parent
	expanded: {
		type: Boolean,
		default: false,
	},

	// Button appearance
	icon: {
		type: String,
		default: 'fa-solid fa-question',
	},
	position: {
		type: String,
		default: 'top-right', // top-right, top-left, bottom-right, bottom-left
		validator: (value) => ['top-right', 'top-left', 'bottom-right', 'bottom-left'].includes(value),
	},
	buttonClass: {
		type: String,
		default: 'fixed bigshadow roundButton text-gray-900',
	},

	// Control visibility and behavior
	disabled: {
		type: Boolean,
		default: false,
	},
	visible: {
		type: Boolean,
		default: true,
	},
	closeOnClickOutside: {
		type: Boolean,
		default: true,
	},

	// Content
	title: {
		type: String,
		default: '',
	},

	// Styling
	zIndex: {
		type: Number,
		default: 1200,
	},
})

const appStore = useAppStore()
// Add 'update:expanded' to the emits array for v-model support
const emit = defineEmits(['expand', 'collapse', 'toggle', 'update:expanded'])

// Writable computed property to handle the v-model binding
const isExpanded = computed({
	get() {
		return props.expanded
	},
	set(newValue) {
		// Emit event to update the parent's v-model
		emit('update:expanded', newValue)
	},
})

const handleClickOutside = () => {
	if (props.closeOnClickOutside) {
		isExpanded.value = false
		emit('collapse')
	}
}

const expandClick = () => {
	isExpanded.value = true
	emit('expand')
	emit('toggle', true)
}

const collapse = () => {
	isExpanded.value = false
	emit('collapse')
	emit('toggle', false)
}

const positionClasses = computed(() => {
	const positions = {
		'top-right': 'top-safe right-6',
		'top-left': 'top-safe left-6',
		'bottom-right': 'bottom-safe right-6',
		'bottom-left': 'bottom-safe left-6',
	}
	return positions[props.position] || positions['top-right']
})

const contentPositionClasses = computed(() => {
	const isTop = props.position.includes('top')
	const isLeft = props.position.includes('left')

	return {
		top: isTop ? '0px' : 'auto',
		bottom: isTop ? 'auto' : '0px',
		left: isLeft ? '0px' : 'auto',
		right: isLeft ? 'auto' : '0px',
	}
})

// Close the dialog when permissions change
watch(
	() => appStore.permissions,
	() => {
		isExpanded.value = false
	},
	{ deep: true },
)

// Expose methods for parent component control
defineExpose({
	expand: () => {
		isExpanded.value = true
	},
	collapse: () => {
		isExpanded.value = false
	},
	toggle: () => {
		isExpanded.value = !isExpanded.value
	},
	isExpanded: computed(() => isExpanded.value),
})
</script>

<template>
	<!-- Use the computed property for the class binding -->
	<div v-if="visible" class="floating-helper" :class="{ expanded: isExpanded, disabled }" @click="handleClickOutside">
		<!-- Floating Button -->
		<button type="button" @click.stop="expandClick" :class="[buttonClass, positionClasses]" :disabled="disabled">
			<i :class="icon"></i>
		</button>

		<!-- Content Panel -->
		<div class="content" :style="{ ...contentPositionClasses }">
			<!-- Header -->
			<div v-if="title" class="header">
				<!-- title as html -->
				<h2 v-html="title"></h2>
				<slot name="subtitle"></slot>
			</div>

			<!-- Slot for custom content -->
			<div class="content-body">
				<slot></slot>
			</div>

			<!-- Close Button -->
			<div class="close" @click.stop="collapse">
				<i class="fa-solid fa-close"></i>
			</div>
		</div>
	</div>
</template>

<style>
.floating-helper .header h2 span {
	display: inline-block;
	font-weight: normal;
	margin-left: 0.5rem;
	opacity: 0.5;
}
</style>

<style scoped>
.top-safe {
	top: max(1.5rem, env(safe-area-inset-top));
}

.bottom-safe {
	bottom: max(1.5rem, env(safe-area-inset-bottom));
}

.bottom-sheet.fullscreen ~ .floating-helper button {
	opacity: 0;
	pointer-events: none;
}

.floating-helper {
	position: fixed;
	top: 0px;
	left: 0px;
	pointer-events: none;
	z-index: 1000;
}
.floating-helper.expanded {
	z-index: 1200;
}
.floating-helper button {
	background: white;
	font-size: 1.25rem;
	transition: all 0.5s ease-out;
	cursor: pointer;
	width: 2.5rem;
	height: 2.5rem;
	pointer-events: all;
}

.floating-helper.expanded button,
.floating-helper.disabled button {
	transform: translateY(-200%);
	opacity: 0;
	pointer-events: none;
}

.floating-helper::after {
	content: '';
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100vw;
	height: 100vh;
	background-color: rgba(0, 0, 0, 0);
	opacity: 0;
	transition: all 0.5s ease-out;
	pointer-events: none;
	backdrop-filter: grayscale(0.2) brightness(0.8);
}

.floating-helper.expanded::after {
	opacity: 1;
	pointer-events: all;
}

.floating-helper .content {
	padding-top: max(1.5rem, calc(env(safe-area-inset-top) + 0.5rem));
	padding-left: 1rem;
	padding-right: 1rem;
	padding-bottom: 1.25rem;
	background-color: rgba(255, 255, 255, 0.7);
	background-color: var(--sheet-background-color);
	box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
	position: fixed;
	width: 100vw;
	transition: all 0.5s ease-out;
	transform: translateY(-150%);
	opacity: 0;
	display: flex;
	pointer-events: none;
	flex-direction: column;
	z-index: 1100;
	max-height: 100vh;
	overflow: auto;
}

.floating-helper.expanded:not(.disabled) .content {
	transform: translateY(0px);
	opacity: 1;
	pointer-events: all;
}

.floating-helper .header h2 {
	margin: 0px;
	margin-bottom: 0.5rem;
}

.floating-helper .content-body {
	flex: 1;
	overflow-y: auto;
}

.floating-helper .close {
	position: absolute;
	top: max(1.5rem, env(safe-area-inset-top));
	right: 1.5rem;
	font-size: 1rem;
	cursor: pointer;
	opacity: 1 !important;
	pointer-events: all;
}

/* Responsive positioning for different screen sizes */
@media (min-width: 768px) {
	.floating-helper .content {
		width: 400px;
		height: auto;
		max-height: 80vh;
		border-radius: 0.5rem;
	}

	/* Position content near button on larger screens */
	.floating-helper .content {
		position: absolute;
	}
}
</style>
